import { useEffect, useMemo, useReducer, useState, useContext } from "react";
import { useParams } from "react-router";
import { useOutletContext } from "react-router-dom";
import {
  Box,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Stack,
  Typography,
  CircularProgress,
  TablePagination,
  TableContainer,
} from "@mui/material";
import AddCommentIcon from "@mui/icons-material/AddComment";

import { LoaderContext } from "../../context/LoaderContext";
import useSendMessagesMutation from "../../hooks/useSendMessagesMutation";
import useGetPatientMessages from "../../hooks/useGetPatientMessages";
import { CustomDatePicker } from "../../components/UI/CustomDatePicker";
import { formatDate } from "../../utils/helpers";

const initialForm = {
  title: "",
  message: "",
  scheduleMode: "now",
  scheduledAt: "",
  channels: { app: false, sms: false, email: false },
};

function formReducer(state, action) {
  switch (action.type) {
    case "RESET":
      return initialForm;
    case "FIELD":
      return { ...state, [action.name]: action.value };
    case "CHANNEL":
      return {
        ...state,
        channels: { ...state.channels, [action.name]: action.value },
      };
    default:
      return state;
  }
}

const PatientMessages = () => {
  const { _id_ } = useParams();
  const loaderContext = useContext(LoaderContext);
  const { trialFilter, setActive, setBreadcrumbTitle, isTrialLocked } = useOutletContext();

  useEffect(() => {
    setActive("messages");
    setBreadcrumbTitle("Messages");
  }, [setActive, setBreadcrumbTitle]);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  const { data, isLoading } = useGetPatientMessages({
    patientId: _id_,
    page: page + 1,
    pageSize,
    trialId: trialFilter,
    enabled: !!trialFilter,
  });

  const messages = data?.results ?? [];
  const total = data?.count ?? 0;

  const sendMessagesMutation = useSendMessagesMutation();

  const [open, setOpen] = useState(false);
  const [form, dispatch] = useReducer(formReducer, initialForm);

  const notificationTypes = useMemo(() => {
    const types = [];
    if (form.channels.app) types.push("App");
    if (form.channels.sms) types.push("SMS");
    if (form.channels.email) types.push("Email");
    return types;
  }, [form.channels]);

  const isValid = useMemo(() => {
    if (!form.title.trim() || !form.message.trim()) return false;
    if (notificationTypes.length === 0) return false;
    if (form.scheduleMode === "schedule" && !form.scheduledAt) return false;
    return true;
  }, [form, notificationTypes.length]);

  const resetForm = () => dispatch({ type: "RESET" });

  const handleCloseCompose = () => {
    setOpen(false);
    resetForm();
  };

  const handleSubmit = () => {
    let notification_time = "now";
    if (form.scheduleMode === "schedule") {
      notification_time = new Date(form.scheduledAt).toISOString().slice(0, 16);
    }

    sendMessagesMutation.mutate(
      {
        patient_id: _id_,
        notification_title: form.title.trim(),
        notification_message: form.message.trim(),
        notification_types: notificationTypes,
        notification_time,
        trial_id: trialFilter,
      },
      {
        onSuccess: handleCloseCompose,
      }
    );
  };

  const busy = isLoading || loaderContext.loaderCount > 0;

  const headerCellSx = {
    backgroundColor: (theme) => theme.palette.grey[100],
    fontWeight: "bold",
    textTransform: "uppercase",
    fontSize: "0.85rem",
    color: "#424242",
    whiteSpace: "nowrap",
  };

  const bodyCellSx = {
    fontSize: "0.875rem",
    whiteSpace: "nowrap",
  };

  return (
    <Paper elevation={1} sx={{ p: 2 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Subject Messages</Typography>

        <Button
          startIcon={<AddCommentIcon />}
          variant="contained"
          size="small"
          onClick={() => setOpen(true)}
          disabled={isTrialLocked}
          data-testid="compose-message-btn"
        >
          New Message
        </Button>
      </Stack>

      {/* Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellSx}>Description</TableCell>
              <TableCell sx={headerCellSx}>Status</TableCell>
              <TableCell sx={headerCellSx}>Type</TableCell>
              <TableCell sx={headerCellSx}>Scheduled Time</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {busy && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            )}

            {!busy && messages.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No messages found</Typography>
                </TableCell>
              </TableRow>
            )}

            {!busy &&
              messages.map((msg, idx) => (
                <TableRow key={idx} hover>
                  <TableCell sx={{ maxWidth: 480 }}>
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                      {msg.description || "-"}
                    </Typography>
                  </TableCell>

                  <TableCell sx={bodyCellSx}>{msg.sent ? "Sent" : "Not Sent"}</TableCell>

                  <TableCell sx={bodyCellSx}>{msg.type}</TableCell>

                  <TableCell sx={bodyCellSx}>{formatDate(msg.scheduled_time)}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box display="flex" justifyContent="flex-end" mt={1}>
        <TablePagination
          component="div"
          rowsPerPageOptions={[10, 20, 50, 100]}
          count={total}
          rowsPerPage={pageSize}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => {
            setPageSize(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Box>

      {/* Compose Dialog */}
      <Dialog open={open} onClose={handleCloseCompose} fullWidth maxWidth="sm">
        <DialogTitle>Send Message</DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2}>
            <FormControl>
              <FormLabel>Notification Channels</FormLabel>
              <FormGroup row>
                {["app", "sms", "email"].map((ch) => (
                  <FormControlLabel
                    key={ch}
                    control={
                      <Checkbox
                        checked={form.channels[ch]}
                        onChange={(e) =>
                          dispatch({
                            type: "CHANNEL",
                            name: ch,
                            value: e.target.checked,
                          })
                        }
                      />
                    }
                    label={ch.toUpperCase()}
                  />
                ))}
              </FormGroup>
            </FormControl>

            <FormControl>
              <FormLabel>Delivery Time</FormLabel>
              <RadioGroup
                row
                value={form.scheduleMode}
                onChange={(e) =>
                  dispatch({
                    type: "FIELD",
                    name: "scheduleMode",
                    value: e.target.value,
                  })
                }
              >
                <FormControlLabel value="now" control={<Radio />} label="Now" />
                <FormControlLabel value="schedule" control={<Radio />} label="Schedule" />
              </RadioGroup>
            </FormControl>

            {form.scheduleMode === "schedule" && (
              <CustomDatePicker
                id="schedule_at"
                variant="outlined"
                size="lg"
                labelFontSize="1rem"
                inputFontSize="1rem"
                label={"Schedule At"}
                value={form.scheduledAt}
                onChange={(val) =>
                  dispatch({
                    type: "FIELD",
                    name: "scheduledAt",
                    value: val,
                  })
                }
                mode={"datetime"}
              />
            )}

            <TextField
              label="Title"
              value={form.title}
              onChange={(e) =>
                dispatch({
                  type: "FIELD",
                  name: "title",
                  value: e.target.value,
                })
              }
              fullWidth
            />

            <TextField
              label="Message"
              value={form.message}
              onChange={(e) =>
                dispatch({
                  type: "FIELD",
                  name: "message",
                  value: e.target.value,
                })
              }
              fullWidth
              multiline
              minRows={4}
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseCompose} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!isValid || sendMessagesMutation.isPending}
            data-testid="send-message-btn"
          >
            {sendMessagesMutation.isPending ? "Sending..." : "Send"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default PatientMessages;
